/**
 * Authentication Service
 * Provides methods for user authentication and session management
 */

import i18n from '../../i18n';
import { API_BASE } from '../../shared/utils/apiConfig';

const au = (key, opts) => i18n.t(`serviceMessages.auth.${key}`, opts);

// Use Worker auth endpoints even when the rest of the app uses /api
const getAuthBase = () => {
  try {
    const api = API_BASE || '';
    // If API points to Express (/api), direct auth to Worker (/functions)
    if (api.includes('/api')) {
      return `${window.location.origin}/functions`;
    }
    return api; // Already pointing to /functions or absolute Worker URL
  } catch (_) {
    return '/functions';
  }
};

const AUTH_BASE = getAuthBase();

const AUTH_TOKEN_KEY = 'qawaem_token';
const AUTH_USER_KEY = 'qawaem_user';

/**
 * Authentication service with backend API integration
 */
const authService = {
  /**
   * Login with username and password
   * @param {Object} credentials - User credentials
   * @param {string} credentials.username - Username
   * @param {string} credentials.password - Password
   * @returns {Promise<Object>} User data and token
   */
  login: async (credentials) => {
    try {
      // Convert username to email format for backend API
      const email = credentials.username.includes('@') 
        ? credentials.username 
        : `${credentials.username}@qawaem.local`;
      
      const response = await fetch(`${AUTH_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password: credentials.password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || au('login_failed'));
      }

      const data = await response.json();
      
      // Store auth data in localStorage
      localStorage.setItem(AUTH_TOKEN_KEY, data.token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify({
        username: data.user.name,
        role: data.user.role,
        id: data.user.id,
        loginTime: new Date().toISOString()
      }));

      return {
        user: {
          username: data.user.name,
          role: data.user.role,
          id: data.user.id
        },
        token: data.token
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Logout the current user
   * @returns {Promise<void>}
   */
  logout: async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      
      if (token) {
        await fetch(`${AUTH_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.warn('Error during logout:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem('qawaem_admin_user');
    }
  },

  /**
   * Register a new user
   * @param {Object} userData - New user data
   * @returns {Promise<Object>} Created user
   */
  register: async (userData) => {
    try {
      const response = await fetch(`${AUTH_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || au('registration_failed'));
      }

      return await response.json();
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  /**
   * Get current user information
   * @returns {Promise<Object>} Current user data
   */
  getCurrentUser: async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      
      if (!token) {
        return null;
      }

      const response = await fetch(`${AUTH_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(AUTH_USER_KEY);
          return null;
        }
        throw new Error(au('failed_get_user'));
      }

      const data = await response.json();
      
      // Update stored user data
      const userData = {
        username: data.user.name,
        role: data.user.role,
        id: data.user.id,
        loginTime: new Date().toISOString()
      };
      // Keep storage minimal; frontend will rely on backend for source of truth
      try { 
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData)); 
      } catch (error) {
        console.error('Error storing user data in localStorage:', error);
      }
      
      return userData;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },
  /**
   * Update current user's profile
   */
  updateMe: async (updates) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) throw new Error(au('not_authenticated'));
    const response = await fetch(`${AUTH_BASE}/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || au('failed_update_profile'));
    }
    const data = await response.json();
    // Sync minimal local cache
    try { 
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify({
        username: data.user.name,
        role: data.user.role,
        id: data.user.id,
        loginTime: new Date().toISOString()
      })); 
    } catch (error) {
      console.error('Error syncing user data to localStorage:', error);
    }
    return data.user;
  },

  /**
   * Change current user's password (backend only)
   */
  changePassword: async ({ currentPassword, newPassword }) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) throw new Error(au('not_authenticated'));
    const response = await fetch(`${AUTH_BASE}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || au('failed_change_password'));
    }
    return await response.json();
  },
  
  /**
   * Create an admin account (initial setup)
   * @param {Object} adminData - Admin data
   * @returns {Promise<Object>} Created admin user
   */
  createAdminAccount: async (adminData) => {
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: adminData.username,
          email: `${adminData.username}@qawaem.local`,
          password: adminData.password,
          role: 'admin'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || au('failed_create_admin'));
      }

      const data = await response.json();
      
      // Store admin data for immediate use in setup flow
      const userData = {
        username: data.name,
        role: data.role,
        id: data.id,
        password: adminData.password // Store temporarily for login
      };
      
      localStorage.setItem('qawaem_admin_user', JSON.stringify(userData));
      
      return userData;
    } catch (error) {
      console.error('Error creating admin account:', error);
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {Promise<boolean>}
   */
  isAuthenticated: async () => {
    try {
      const user = await authService.getCurrentUser();
      return !!user;
    } catch (error) {
      return false;
    }
  },

  /**
   * Get authentication headers for API requests
   * @returns {Object} Headers object with Authorization if token exists
   */
  getAuthHeader: () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }
};

export default authService;


